import { useMemo } from 'react'
import { Card, Col, List, Row, Space, Statistic, Tag, Typography } from 'antd'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import {
  maintenanceKpiApi, maintenanceRequestsApi, pmSchedulesApi, sparesApi,
  workOrdersApi,
} from '@/features/maintenance/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { usePermission } from '@/shared/hooks/usePermission'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'

const { Text } = Typography

/**
 * Spec §A / §B / §F — Light executive landing for managers, supervisors,
 * and plant heads. Jumps off into the deeper screens. Heavy KPI dashboards
 * are reserved for Phase 3.
 */
export default function MaintenanceHomePage() {
  const { plantId } = usePlantId()
  const canSeeKpi = usePermission('maintenance.read')

  const { data: requests } = useQuery({
    queryKey: ['maintenance-requests-summary', plantId],
    enabled: !!plantId,
    queryFn: () => maintenanceRequestsApi.list({ plant_id: plantId, limit: 200 }),
  })
  const { data: workOrders } = useQuery({
    queryKey: ['work-orders-summary', plantId],
    enabled: !!plantId,
    queryFn: () => workOrdersApi.list({ plant_id: plantId, only_open: false, limit: 500 }),
  })
  const { data: pmSchedules } = useQuery({
    queryKey: ['pm-schedules-summary', plantId],
    enabled: !!plantId,
    queryFn: () => pmSchedulesApi.list({ plant_id: plantId, limit: 500 }),
  })
  const { data: spares } = useQuery({
    queryKey: ['spares-summary'],
    queryFn: () => sparesApi.list({ low_stock_only: true, limit: 500 }),
  })
  const { data: kpi } = useQuery({
    queryKey: ['maintenance-kpi-summary', plantId],
    enabled: !!plantId && canSeeKpi,
    queryFn: () => maintenanceKpiApi.summary({ plant_id: plantId }),
  })

  const buckets = useMemo(() => {
    const w = workOrders ?? []
    return {
      open:        w.filter((x) => x.status !== 'CLOSED' && x.status !== 'COMPLETED').length,
      inProgress:  w.filter((x) => x.status === 'IN_PROGRESS').length,
      waitingSpr:  w.filter((x) => x.status === 'WAITING_FOR_SPARES').length,
      critical:    w.filter((x) => x.priority === 'CRITICAL'),
      pendingClose:w.filter((x) => x.status === 'COMPLETED'),
    }
  }, [workOrders])

  const overdueCount = (pmSchedules ?? []).filter((s) => s.overdue_flag).length
  const recentRequests = (requests ?? []).slice(0, 5)

  return (
    <div>
      <PageHeader
        title="Maintenance Home"
        subtitle="Quick read on requests, work orders, PM compliance and stock alerts."
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card><Statistic title="Open work orders" value={buckets.open} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="In progress" value={buckets.inProgress} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="Waiting for spares" value={buckets.waitingSpr} valueStyle={{ color: '#faad14' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="Pending closure approval" value={buckets.pendingClose.length} valueStyle={{ color: '#1677ff' }} /></Card>
        </Col>
      </Row>

      {kpi && (
        <Card size="small" title="KPIs (period to date)" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} md={4}><Statistic title="MTTR (min)" value={kpi.mttr_minutes} /></Col>
            <Col xs={12} md={4}><Statistic title="MTBF (h)" value={kpi.mtbf_hours} /></Col>
            <Col xs={12} md={4}><Statistic title="PM compliance %" value={kpi.pm_compliance_pct} suffix="%" /></Col>
            <Col xs={12} md={4}><Statistic title="Repeat breakdowns" value={kpi.repeat_breakdown_count} /></Col>
            <Col xs={12} md={4}><Statistic title="Emergency %" value={kpi.emergency_percent} suffix="%" /></Col>
            <Col xs={12} md={4}><Statistic title="PM overdue" value={kpi.pm_overdue} /></Col>
          </Row>
        </Card>
      )}

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card size="small" title="Critical / High priority work orders" extra={
            <Link to="/maintenance/work-orders">Open board →</Link>
          } style={{ marginBottom: 16 }}>
            {buckets.critical.length === 0
              ? <Text type="secondary">No critical or high priority WOs open.</Text>
              : <List
                size="small"
                dataSource={buckets.critical.slice(0, 8)}
                renderItem={(wo) => (
                  <List.Item>
                    <Space>
                      <Text strong>{wo.work_order_no}</Text>
                      <MaintenanceStatusBadge status={wo.priority} />
                      <MaintenanceStatusBadge status={wo.status} />
                    </Space>
                  </List.Item>
                )}
              />}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small" title="Recent breakdown requests" extra={
            <Link to="/maintenance/requests">All requests →</Link>
          } style={{ marginBottom: 16 }}>
            {recentRequests.length === 0
              ? <Text type="secondary">No requests yet.</Text>
              : <List
                size="small"
                dataSource={recentRequests}
                renderItem={(r) => (
                  <List.Item>
                    <Space>
                      <Text strong>{r.request_no}</Text>
                      <MaintenanceStatusBadge status={r.priority} />
                      <MaintenanceStatusBadge status={r.status} />
                      {r.auto_promoted_to_wo && <Tag color="purple">AUTO PROMOTED</Tag>}
                    </Space>
                  </List.Item>
                )}
              />}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small" title="PM" extra={<Link to="/maintenance/pm">Open calendar →</Link>}>
            <Statistic title="Overdue PMs" value={overdueCount} valueStyle={{ color: overdueCount > 0 ? '#cf1322' : undefined }} />
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small" title="Stock alerts" extra={<Link to="/maintenance/spares">Spare desk →</Link>}>
            <Statistic
              title="Spares at or below reorder level"
              value={spares?.length ?? 0}
              valueStyle={{ color: (spares?.length ?? 0) > 0 ? '#faad14' : undefined }} />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
